import { type FC } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { MarketingShell, MarketingHeader } from './MarketingShell';

/**
 * /how-it-works — the audit pipeline, step by step. Public, unauthenticated.
 */

const STEPS: Array<{ n: string; title: string; body: string }> = [
  {
    n: '01',
    title: 'Upload',
    body: 'Enter the loan number and attach the LOS export. Borrower, product, and branch are read straight from the LOS — then add the loan documents (CAM, KYC, bank statement, sanction letter, legal & valuation reports, and more).',
  },
  {
    n: '02',
    title: 'Parse',
    body: 'Each document is classified and read with OCR and field extraction. Low-confidence pages are flagged for review rather than guessed.',
  },
  {
    n: '03',
    title: 'Validate',
    body: 'The 12-point engine reconciles the CAM against the LOS and the documents — completeness, cross-checks, CIBIL, eligibility recompute, deviations, sanction conditions, legal and valuation.',
  },
  {
    n: '04',
    title: 'Review',
    body: 'Anything the engine cannot confirm on its own lands in the review queue, scoped per loan number, where an auditor approves, corrects, or rejects it.',
  },
  {
    n: '05',
    title: 'Findings & score',
    body: 'Issues surface as evidence-linked findings with a severity, and the file gets a single audit score so clean files and exceptions are obvious.',
  },
  {
    n: '06',
    title: 'Report',
    body: 'Generate a shareable audit report that mirrors the on-screen result — the checklist, the issues, the evidence, and the final score.',
  },
];

export const HowItWorksPage: FC = () => (
  <MarketingShell>
    <MarketingHeader
      eyebrow="How it Works"
      title="From upload to audited in six steps."
      lead="The same pipeline runs on every loan file — deterministic where it can be, AI-assisted where judgement helps, and always evidence-backed."
    />

    <div className="mt-12">
      <h2 className="font-display text-title font-bold text-on-surface mb-4">The pipeline</h2>
      <ol className="space-y-4">
        {STEPS.map((s) => (
          <li key={s.n} className="flex gap-5 bg-surface-container-lowest border border-outline-variant rounded-2xl p-6">
            <span className="font-display text-display font-black text-primary/30 leading-none tabular-nums shrink-0">
              {s.n}
            </span>
            <div>
              <h3 className="font-display text-heading font-bold text-on-surface">{s.title}</h3>
              <p className="mt-1.5 text-label text-on-surface-variant leading-relaxed">{s.body}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>

    <div className="mt-12 flex justify-center">
      <Link
        to="/login"
        className="h-11 px-6 inline-flex items-center gap-2 rounded-lg bg-primary text-on-primary text-label font-semibold hover:bg-primary-container transition-colors"
      >
        Get started
        <ArrowRight className="w-4 h-4" aria-hidden="true" />
      </Link>
    </div>
  </MarketingShell>
);

export default HowItWorksPage;
